import { UserPhoto } from '@/components/UserPhoto'
import { Avatar, AvatarFallback, AvatarGroup, AvatarGroupCount } from '@/components/ui/avatar'
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card'
import { cn } from '@/lib/utils'

export interface StackPerson {
    id: string
    name: string
    email?: string
    /** Why this person is here, e.g. "group:Approvers" or "submitter's manager". */
    via?: string
    /** Optional per-person state ring (e.g. who has already decided). */
    state?: 'done' | 'refused' | 'waiting'
}

export function initialsOf(name: string): string {
    return (
        name
            .split(/\s+/)
            .filter(Boolean)
            .slice(0, 2)
            .map((w) => w[0]?.toUpperCase() ?? '')
            .join('') || '?'
    )
}

const ringClass: Record<NonNullable<StackPerson['state']>, string> = {
    done: 'ring-2 ring-accent-strong',
    refused: 'ring-2 ring-destructive',
    waiting: 'ring-2 ring-border',
}

function viaLabel(via?: string): string {
    if (!via) return ''
    if (via.startsWith('group:')) return `Group · ${via.slice(6)}`
    if (via.startsWith('field:')) return 'Named on the form'
    if (via === 'user') return 'Named reviewer'
    return via.charAt(0).toUpperCase() + via.slice(1)
}

interface Props {
    people: StackPerson[]
    /** Avatars shown before collapsing into "+N". */
    max?: number
    className?: string
}

/**
 * Overlapping avatars with a "+N" overflow chip. Hovering shows everyone with
 * their reason for being on the step (the "via" captured when it was routed).
 */
export function AvatarStack({ people, max = 4, className }: Props) {
    if (people.length === 0) {
        return <span className="text-xs text-muted-foreground">No one assigned</span>
    }
    const shown = people.slice(0, max)
    const extra = people.length - shown.length
    return (
        <HoverCard openDelay={120}>
            <HoverCardTrigger asChild>
                <button type="button" className={cn('rounded-full text-left focus-visible:outline-2 focus-visible:outline-ring', className)}
                    aria-label={`${people.length} ${people.length === 1 ? 'person' : 'people'}: ${people.map((p) => p.name).join(', ')}`}>
                    <AvatarGroup>
                        {shown.map((p) => (
                            <Avatar key={p.id} className={cn(p.state && ringClass[p.state])}>
                                <UserPhoto id={p.id} />
                                <AvatarFallback className="bg-primary/10 text-[11px] font-semibold text-primary">
                                    {initialsOf(p.name)}
                                </AvatarFallback>
                            </Avatar>
                        ))}
                        {extra > 0 && <AvatarGroupCount>+{extra}</AvatarGroupCount>}
                    </AvatarGroup>
                </button>
            </HoverCardTrigger>
            <HoverCardContent align="start" className="w-72">
                <ul className="flex max-h-64 flex-col gap-2 overflow-y-auto">
                    {people.map((p) => (
                        <li key={p.id} className="flex items-center gap-2.5">
                            <Avatar className="size-7">
                                <UserPhoto id={p.id} />
                                <AvatarFallback className="bg-primary/10 text-[10px] font-semibold text-primary">
                                    {initialsOf(p.name)}
                                </AvatarFallback>
                            </Avatar>
                            <div className="min-w-0 flex-1">
                                <div className="truncate text-sm font-medium">{p.name}</div>
                                <div className="truncate text-xs text-muted-foreground">
                                    {[p.email, viaLabel(p.via)].filter(Boolean).join(' · ')}
                                </div>
                            </div>
                            {p.state === 'done' && <span className="text-xs font-medium text-primary">Decided</span>}
                            {p.state === 'refused' && <span className="text-xs font-medium text-destructive">Refused</span>}
                        </li>
                    ))}
                </ul>
            </HoverCardContent>
        </HoverCard>
    )
}
